<template>
  <section class="bg-white">
    <div class="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <!-- Header -->
      <div class="mx-auto flex w-full max-w-[720px] flex-col items-center gap-3 text-center">
        <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">How it works</span>
        <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
          From blank to mailbox in three steps
        </h2>
        <p class="text-pretty text-lg text-gray-700">
          Size and template first, so nothing you design can print wrong. Then export, or send straight through your provider.
        </p>
      </div>

      <!-- Steps -->
      <ol class="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-6">
        <!-- Step 1 -->
        <li class="flex flex-col gap-5">
          <div
            class="relative flex h-[190px] flex-col justify-center gap-3 overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-b from-white to-gray-100 p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]"
          >
            <span class="absolute left-3 top-3 rounded-full bg-primary-100 px-2.5 py-0.5 font-mono text-xs font-semibold text-primary-700">01</span>
            <!-- Size chips + postcard thumbnails -->
            <div class="flex flex-wrap gap-1.5 pt-4">
              <span
                v-for="size in ['4×6', '6×9', '6×11', 'Letter']"
                :key="size"
                class="rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                :class="size === '4×6' ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-400 bg-white text-gray-800'"
                >{{ size }}</span
              >
            </div>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="thumb in step1Thumbs"
                :key="thumb.label"
                class="flex h-12 flex-col justify-end overflow-hidden rounded p-1 text-white"
                :style="{ background: thumb.bg }"
                aria-hidden="true"
              >
                <span class="text-[7px] font-bold leading-tight">{{ thumb.label }}</span>
              </div>
            </div>
          </div>
          <h3 class="text-lg font-semibold text-gray-950">Pick a size and template</h3>
          <p class="text-sm text-gray-700">
            Bleed, safe zone and the address block are already placed for every size. Start from ours or from your brand kit.
          </p>
        </li>

        <!-- Step 2 -->
        <li class="flex flex-col gap-5">
          <div
            class="relative flex h-[190px] items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-b from-white to-gray-100 p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]"
          >
            <span class="absolute left-3 top-3 rounded-full bg-primary-100 px-2.5 py-0.5 font-mono text-xs font-semibold text-primary-700">02</span>
            <!-- Postcard with cursor -->
            <div class="relative">
              <div
                class="relative flex h-[110px] w-[160px] flex-col justify-end overflow-hidden rounded p-2 text-white shadow-[0px_8px_24px_-4px_rgba(16,24,40,0.3)]"
                style="background: linear-gradient(160deg, #6941c6, #b692f6)"
                aria-label="Postcard being edited"
              >
                <span class="text-[9px] font-bold leading-tight">Just listed on Maple Street</span>
                <span class="text-[7px] opacity-80"
                  >Hi <em class="rounded bg-white/20 px-0.5 not-italic">{{ first_name }}</em
                  >, a home like yours sold in 9 days.</span
                >
              </div>
              <!-- Cursor -->
              <svg
                class="absolute right-3 top-6 size-4 drop-shadow-md"
                viewBox="0 0 24 24"
                fill="white"
                stroke="#101828"
                stroke-width="1.5"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4 4l7 16 2-7 7-2z" />
              </svg>
              <!-- Tooltip -->
              <div class="absolute -bottom-2 right-0 flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1 shadow-sm">
                <span
                  class="size-2 rounded-sm bg-primary-600"
                  aria-hidden="true"
                />
                <!-- <span class="font-mono text-[9px] font-semibold text-gray-800">Inter · 36 pt · {{ "{{" }}first_name{{'}}'}}</span> -->
              </div>
            </div>
          </div>
          <h3 class="text-lg font-semibold text-gray-950">Design, or let your users design</h3>
          <p class="text-sm text-gray-700">
            Text, shapes, images, layers and merge fields in an editor that feels like the tools people already know. Images are checked at print
            size.
          </p>
        </li>

        <!-- Step 3 -->
        <li class="flex flex-col gap-5">
          <div
            class="relative flex h-[190px] items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-b from-white to-gray-100 p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]"
          >
            <span class="absolute left-3 top-3 rounded-full bg-primary-100 px-2.5 py-0.5 font-mono text-xs font-semibold text-primary-700">03</span>
            <!-- Export diagram -->
            <div class="flex flex-col items-center gap-4">
              <div class="flex items-center gap-3">
                <!-- Files -->
                <div class="flex flex-col gap-1.5">
                  <div class="flex items-center gap-1.5 rounded-md bg-gray-950 px-2.5 py-1">
                    <span
                      class="size-1.5 rounded-full bg-[#34d399]"
                      aria-hidden="true"
                    />
                    <span class="font-mono text-[9px] text-[#d1d5db]">front.html</span>
                  </div>
                  <div class="flex items-center gap-1.5 rounded-md bg-gray-950 px-2.5 py-1">
                    <span
                      class="size-1.5 rounded-full bg-[#34d399]"
                      aria-hidden="true"
                    />
                    <span class="font-mono text-[9px] text-[#d1d5db]">back.html</span>
                  </div>
                </div>
                <!-- Arrow -->
                <svg
                  class="size-5 text-primary-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
                <!-- Providers -->
                <div class="flex flex-col gap-1.5">
                  <div class="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1">
                    <img
                      :src="LobLogo"
                      alt="Lob"
                      class="h-3.5 w-auto"
                    />
                  </div>
                  <div class="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1">
                    <img
                      :src="PostgridLogo"
                      alt="PostGrid"
                      class="h-3.5 w-auto"
                    />
                  </div>
                </div>
              </div>
              <!-- Sent badge -->
              <span class="bg-success-50 text-success-600 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
                <svg
                  class="size-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
                Sent · 1,240 pieces
              </span>
            </div>
          </div>
          <h3 class="text-lg font-semibold text-gray-950">Export HTML, or send straight through</h3>
          <p class="text-sm text-gray-700">
            Download front and back HTML that passes preflight, or push to Lob, PostGrid and Direct Mail Manager with merge variables attached.
          </p>
        </li>
      </ol>
    </div>
  </section>
</template>

<script setup lang="ts">
import LobLogo from "@/../images/integration-icons/lob-icon.svg";
import PostgridLogo from "@/../images/integration-icons/postgrid-icon.svg";

const step1Thumbs = [
  { label: "Just listed", bg: "linear-gradient(160deg, #6941c6, #b692f6)" },
  { label: "Check-up", bg: "linear-gradient(160deg, #0F766E, #5EEAD4)" },
  { label: "We miss you", bg: "linear-gradient(160deg, #C2410C, #FDBA74)" },
];
</script>
